import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import {
  useGridApiRef,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GRID_TREE_DATA_GROUPING_FIELD,
} from '@mui/x-data-grid-premium';
import SyncIcon from '@mui/icons-material/Sync';
import LayersIcon from '@mui/icons-material/Layers';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import StyledDataGrid from '../../components/DataGrid/DataGridStyles';
import EmptyState from '../../components/EmptyState/EmptyState';
import { CustomGridFooter } from '../../components/DataGrid/CustomGridFooter';
import { useTableSearch } from '../../hooks/useTableSearch';
import { useSyncOperations } from '../../hooks/useSyncOperations';
import { useCellUndoRedo } from '../../components/UndoRedo/useCellUndoRedo';
import UndoRedoButtons from '../../components/UndoRedo/UndoRedoButtons';
import Toolbar from '../../components/Toolbar/Toolbar';
import AutosizeColumnsToggle from '../../components/DataGrid/AutosizeColumnsToggle';
import GroupToggle from '../../components/DataGrid/GroupToggle';
import { useCustodianData } from './useCustodianData';
import { useCustodianColumns } from './useCustodianColumns';
import { PortfolioDataTable } from '../../types/generatedTypes';
import { useViewButtons } from '../../components/Views/useViewButtons';
import { useViewManagement } from '../../hooks/useViewManagement/useViewManagement';
import { CustodianView } from '../../hooks/useViewManagement/types';
import SaveViewDialog from '../../components/Views/SaveViewDialog';
import SavedViewsPanel from '../../components/Views/SavedViewsPanel';
import ActiveViewIndicator from '../../components/Views/ActiveViewIndicator';
import { PAGE_TYPES } from '../../constants/pageTypes';
import ViewContextMenu from '../../components/Views/ViewContextMenu';
import ManageGroupsModal from '../../components/Groups/ManageGroupsModal';
import {
  transformDataForGrouping,
  getTreeDataPath,
  convertGridColumnsToGroupColumns,
  getRowIdAsString,
} from '../../components/Groups/groupUtils';
import { useGroupingColDef } from '../../components/Groups/useManageGroups';
import { createPinnedColumnsHandler } from '../../utils/gridPinnedColumnsHelper';
import {
  createStateChangeHandler,
  useFilteredSelectionModel,
} from '../../components/Groups/gridStateHelper';
import { useGroupCheckboxHandler } from '../../components/Groups/useGroupCheckboxHandler';

// Re-export for backwards compatibility
export type CustodianSavedView = CustodianView;

export const CustodianTablePage: React.FC = () => {
  const apiRef = useGridApiRef();

  const { isSyncingPortfolioAndCustodian, syncPortfolioCustodianFromIllio } =
    useSyncOperations();

  // State to track current display data for selection handler
  const [currentDisplayData, setCurrentDisplayData] = useState<
    PortfolioDataTable[]
  >([]);

  // First get basic custodian data
  const {
    custodianData,
    loading,
    selectedRows,
    autosizeColumns,
    setAutosizeColumns,
    columnVisibilityModel,
    setColumnVisibilityModel,
    fetchCustodianData,
    updateCustodianRecord,
    handleToggleUploadTransactions,
    handleExport,
    handleSyncPortfolioCustodianFromIllio,
    handleRowSelectionChange,
    expandGroups,
    // Groups
    groups,
    setGroups,
    isGroupsModalOpen,
    isGroupingEnabled,
    setIsGroupingEnabled,
    availableRowsForGroups,
    handleOpenGroupsModal,
    handleCloseGroupsModal,
    handleSaveGroups,
  } = useCustodianData({
    apiRef,
    displayData: currentDisplayData,
    syncPortfolioCustodianFromIllio,
  });

  const { filteredData, searchTerm, handleSearch } =
    useTableSearch<PortfolioDataTable>(custodianData);

  // Add view management hook
  const viewManager = useViewManagement({
    pageType: PAGE_TYPES.CUSTODIAN,
    apiRef,
    searchTerm,
    selectedRows,
    autosizeColumns,
    displayData: filteredData,
    handleSearch,
    setSelectedRows: (rows: string[]) => {
      const model = { ids: new Set(rows), type: 'include' as const };
      handleRowSelectionChange(model);
    },
    setAutosizeColumns,
    isGroupingEnabled,
    setIsGroupingEnabled,
    groups,
    setGroups,
  });

  // Apply selected rows filtering when active view has selections
  const baseDisplayData =
    viewManager.filteredDisplayData as PortfolioDataTable[];

  // Transform data for grouping
  const displayData = useMemo(() => {
    return transformDataForGrouping(baseDisplayData, groups, isGroupingEnabled);
  }, [baseDisplayData, groups, isGroupingEnabled]);

  // Calculate actual data row count (excluding group headers)
  const actualRowCount = useMemo(() => {
    return displayData.filter(row => !row.isGroup).length;
  }, [displayData]);

  // Sync currentDisplayData with displayData for selection handler
  useEffect(() => {
    setCurrentDisplayData(displayData);
  }, [displayData]);

  // Create filtered rowSelectionModel to preserve selections during search
  const getRowIdCallback = useCallback(
    (row: PortfolioDataTable) => getRowIdAsString(row),
    []
  );
  const rowSelectionModel = useFilteredSelectionModel(
    selectedRows,
    displayData,
    getRowIdCallback
  );

  // Override applyPinnedColumns to ensure protected columns are always first
  const originalApplyPinnedColumns = viewManager.applyPinnedColumns;
  viewManager.applyPinnedColumns = useCallback(
    createPinnedColumnsHandler(originalApplyPinnedColumns, isGroupingEnabled),
    [originalApplyPinnedColumns, isGroupingEnabled]
  );

  // Initialize undo/redo system
  const {
    wrappedProcessRowUpdate: processRowUpdate,
    undo,
    redo,
    canUndo,
    canRedo,
    getUndoActionInfo,
    getRedoActionInfo,
  } = useCellUndoRedo({
    processRowUpdate: updateCustodianRecord,
    getData: () => custodianData,
  });

  // Generate columns using the custom hook
  const { columns } = useCustodianColumns({
    autosizeColumns,
    isGroupingEnabled,
    onToggleUploadTransactions: handleToggleUploadTransactions,
  });

  // Get grouping column definition
  const groupingColDef = useGroupingColDef(isGroupingEnabled);

  // Group checkbox click handler
  const handleCellClick = useGroupCheckboxHandler({
    displayData,
    selectedRows,
    expandGroups,
    onSelectionChange: handleRowSelectionChange,
    getRowId: row => (row._id ?? row.id)?.toString(),
  });

  useEffect(() => {
    fetchCustodianData();
  }, [fetchCustodianData]);

  // Get view buttons from hook
  const viewButtons = useViewButtons({
    onSaveView: viewManager.openSaveDialog,
    onOpenSavedViews: viewManager.openSavedViewsPanel,
    loading: loading,
    disabled: false,
  });

  // Helper function to track view modifications
  const updateViewModifications = React.useCallback(() => {
    const modifications = viewManager.compareWithAppliedView();
    viewManager.setViewModifications(modifications);
  }, [viewManager]);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: 2,
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Toolbar
          onSearch={handleSearch}
          placeholder="Search custodians..."
          searchTerm={searchTerm}
          disabled={loading || isSyncingPortfolioAndCustodian}
          onExport={handleExport}
          buttons={[
            ...viewButtons,
            {
              variant: 'text',
              label: 'Sync from Illio',
              color: 'secondary',
              onClick: handleSyncPortfolioCustodianFromIllio,
              icon: isSyncingPortfolioAndCustodian ? (
                <CircularProgress size={20} />
              ) : (
                <SyncIcon />
              ),
              disabled: loading || isSyncingPortfolioAndCustodian,
              tooltip: 'Synchronize custodian data from Illio',
            },
            {
              variant: 'text',
              label: 'Groups',
              color: 'secondary',
              onClick: handleOpenGroupsModal,
              icon: <LayersIcon />,
              disabled: loading,
              tooltip: 'Organize custodians into groups',
            },
          ]}
        />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            mt: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <UndoRedoButtons
              canUndo={canUndo}
              canRedo={canRedo}
              onUndo={undo}
              onRedo={redo}
              undoDescription={getUndoActionInfo()?.description}
              redoDescription={getRedoActionInfo()?.description}
              size="small"
            />
            <AutosizeColumnsToggle
              checked={autosizeColumns}
              onChange={setAutosizeColumns}
            />
            <GroupToggle
              checked={isGroupingEnabled}
              onChange={setIsGroupingEnabled}
              disabled={groups.length === 0}
            />
          </Box>
          {viewManager.activeView && (
            <ActiveViewIndicator
              view={viewManager.activeView}
              modifications={viewManager.viewModifications}
              onClose={viewManager.clearActiveView}
              onUpdateView={viewManager.updateCurrentView}
            />
          )}
        </Box>
      </Box>

      <Box sx={{ height: 'calc(100vh - 330px)', width: '100%' }}>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <CircularProgress />
          </Box>
        ) : actualRowCount === 0 ? (
          <EmptyState
            icon={
              <AccountBalanceIcon
                sx={{ fontSize: 80, color: 'text.secondary' }}
              />
            }
            title="No Custodian Data"
            description="Sync custodian data from Illio to begin managing custodian accounts"
          />
        ) : (
          <StyledDataGrid
            key={isGroupingEnabled ? 'grouped-grid' : 'normal-grid'}
            apiRef={apiRef}
            rows={displayData}
            rowCount={actualRowCount}
            columns={columns}
            processRowUpdate={processRowUpdate}
            getRowId={row => row._id || row.id}
            getRowClassName={params =>
              params.row.isGroup ? 'group-row' : 'data-row'
            }
            checkboxSelection
            isRowSelectable={params => !params.row.isGroup}
            disableRowSelectionOnClick
            rowSelectionModel={rowSelectionModel}
            treeData={isGroupingEnabled}
            getTreeDataPath={isGroupingEnabled ? getTreeDataPath : undefined}
            groupingColDef={groupingColDef}
            defaultGroupingExpansionDepth={isGroupingEnabled ? 1 : undefined}
            columnVisibilityModel={columnVisibilityModel}
            initialState={{
              pinnedColumns: {
                left: [
                  GRID_CHECKBOX_SELECTION_COL_DEF.field,
                  ...(isGroupingEnabled
                    ? [GRID_TREE_DATA_GROUPING_FIELD, 'group_name']
                    : []),
                ],
              },
            }}
            slots={{
              footerRowCount: CustomGridFooter,
            }}
            slotProps={{
              footerRowCount: {
                isGroupingEnabled,
                groups,
              },
            }}
            onCellClick={handleCellClick}
            onRowSelectionModelChange={handleRowSelectionChange}
            onSortModelChange={() => {
              updateViewModifications();
            }}
            onFilterModelChange={() => {
              updateViewModifications();
            }}
            onColumnVisibilityModelChange={newModel => {
              setColumnVisibilityModel(newModel);
              updateViewModifications();
            }}
            onPinnedColumnsChange={() => {
              if (viewManager.activeView) {
                requestAnimationFrame(() => {
                  updateViewModifications();
                });
              }
            }}
            onStateChange={createStateChangeHandler(apiRef, isGroupingEnabled)}
          />
        )}
      </Box>

      <SaveViewDialog
        open={viewManager.isSaveViewDialogOpen}
        onClose={viewManager.closeSaveDialog}
        onSave={viewManager.saveView}
        editingView={viewManager.editingView}
        editMode={viewManager.editMode}
        currentSearchTerm={searchTerm}
        autosizeColumns={autosizeColumns}
        isGroupingEnabled={isGroupingEnabled}
        selectedRows={selectedRows}
        savedViews={viewManager.savedViews}
        columnFilters={viewManager.getColumnFilters()}
        sortModel={viewManager.getSortModel()}
        columnVisibility={viewManager.getColumnVisibility()}
        pinnedColumns={viewManager.getPinnedColumns()}
        columns={columns}
      />

      <SavedViewsPanel
        views={viewManager.savedViews}
        onViewClick={viewManager.handleViewClick}
        onSaveClick={viewManager.openSaveDialog}
        onMenuClick={viewManager.handleMenuClick}
        activeView={viewManager.activeView}
        open={viewManager.isSavedViewsPanelOpen}
        onClose={viewManager.closeSavedViewsPanel}
      />

      <ViewContextMenu
        anchorEl={viewManager.contextMenuAnchor}
        open={Boolean(viewManager.contextMenuAnchor)}
        onClose={viewManager.handleCloseContextMenu}
        onApply={viewManager.handleApplyView}
        onRename={viewManager.handleRenameView}
        onUpdate={viewManager.handleUpdateView}
        onDelete={viewManager.handleDeleteView}
      />

      <ManageGroupsModal
        open={isGroupsModalOpen}
        onClose={handleCloseGroupsModal}
        onSave={handleSaveGroups}
        availableRows={availableRowsForGroups}
        existingGroups={groups}
        title="Manage Groups"
        columns={useMemo(
          () => convertGridColumnsToGroupColumns(columns),
          [columns]
        )}
        hiddenColumns={[
          'account_number',
          'portfolio_id',
          'custodian_id',
          'leading_currency',
          'group_name',
        ]}
      />
    </Box>
  );
};
